import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { MoldService } from '../../../service/api/mold-service';
import {
  mapMaintenanceListSubmittedByFromApi,
  mapMaintenanceRecordSubmittedByFromApi,
} from '../map-maintenance-api-fields';

const { MOLDMAINTENANCE_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'moldmaintenance';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
    fromDate: null,
    toDate: null,
    isReleased: 1, // 1 = Active (not released) — matches previous hardcoded GET_ALL/1
    recordFilter: 1, // 0 = All Molds, 1 = Open Records, 2 = Molds With Records (latest CLOSED)
  },
  maintenanceLoading: false,
  filterVisible: false,
  tileView: false,
  selectedMoldMaintenance: null,
  editModalVisible: false,
  moldMaintenanceRecordsData: [],
};

export const fetchMoldsByStatus = createAsyncThunk(
  'mold/fetchMoldsByStatus',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await MoldService.getAllToolDetailsByMoldInfoIDWithOpenFilter(
        filters.isReleased,
        filters.recordFilter,
      );
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load mold receiving');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load  mold receiving');
    }
  },
);

export const fetchMoldReceiving = createAsyncThunk(
  'moldMaintenace/fetchMoldReceiving',
  async (filters = {}, { rejectWithValue }) => {
    try {
      // Date-range filter still uses the original received-date API
      if (filters?.fromDate && filters?.toDate) {
        const response = await MoldService.getAllMold(filters);
        if (response.status === 200) {
          return response.response;
        }
        return rejectWithValue(response.errorMessage?.message || 'Failed to load mold maintenance');
      }

      const response = await MoldService.getAllToolDetailsByMoldInfoIDWithOpenFilter(
        filters?.isReleased ?? 1,
        filters?.recordFilter ?? 1,
      );
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load mold maintenance');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load mold maintenance');
    }
  },
);

export const addMoldMaintenance = createAsyncThunk(
  'moldMaintenance/addMoldMaintenance',
  async (moldMaintenancePayload, { rejectWithValue }) => {
    try {
      // Return the static mold data
      const response = await MoldService.saveUpdateMaintenance(moldMaintenancePayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add mold maintenance');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add mold maintenance');
    }
  },
);

export const editMoldMaintenance = createAsyncThunk(
  'moldMaintenance/editMoldMaintenance',
  async (moldMaintenancePayload, { rejectWithValue }) => {
    try {
      const response = await MoldService.saveUpdateMaintenance(moldMaintenancePayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to update mold maintenance',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update mold maintenance');
    }
  },
);

const getMaintenanceId = record =>
  Number(
    record?.maintenanceID ??
      record?.MaintenanceID ??
      record?.maintenanceId ??
      record?.id ??
      record?.ID ??
      0,
  );

const toFormMaintenance = (detail, fallbackId) => {
  const mappedDetail = mapMaintenanceRecordSubmittedByFromApi(detail);
  const version = String(mappedDetail?.version ?? mappedDetail?.Version ?? '')
    .trim()
    .toUpperCase();
  const moldMaintenanceData = {
    ...mappedDetail,
    id:
      mappedDetail?.id ??
      mappedDetail?.ID ??
      mappedDetail?.maintenanceID ??
      mappedDetail?.MaintenanceID ??
      mappedDetail?.maintenanceId ??
      fallbackId,
    isClosed: version === 'CLOSED',
  };
  delete moldMaintenanceData.ID;
  delete moldMaintenanceData.maintenanceID;
  delete moldMaintenanceData.MaintenanceID;
  delete moldMaintenanceData.maintenanceId;
  return moldMaintenanceData;
};

export const editMoldMaintenanceData = createAsyncThunk(
  'moldMaintenance/editMoldMaintenanceData',
  async (moldReceiving, { rejectWithValue }) => {
    try {
      const focusedMaintenanceId = Number(moldReceiving?.focusedMaintenanceId || 0);
      const receiving = { ...(moldReceiving || {}) };
      delete receiving.focusedMaintenanceId;

      const historyResponse = await MoldService.getAllMoldMaintenanceByToolId(receiving.toolID);

      if (historyResponse.status !== 200) {
        return rejectWithValue(
          historyResponse.errorMessage?.message || 'Failed to load mold maintenance history',
        );
      }

      const history = mapMaintenanceListSubmittedByFromApi(historyResponse.response || []);
      const records = Array.isArray(history) ? history : history ? [history] : [];

      const loadDetail = async maintenanceId => {
        const detailResponse = await MoldService.getAllMoldMaintenanceByToolIdAndId(
          receiving.toolID,
          maintenanceId,
        );
        if (detailResponse.status === 200) {
          return Array.isArray(detailResponse.response)
            ? detailResponse.response[0]
            : detailResponse.response;
        }
        return records.find(record => getMaintenanceId(record) === maintenanceId) || null;
      };

      if (focusedMaintenanceId > 0) {
        const detail = await loadDetail(focusedMaintenanceId);
        if (!detail) {
          return rejectWithValue('Maintenance record not found');
        }
        return {
          moldReceivingData: receiving,
          moldMaintenanceData: toFormMaintenance(detail, focusedMaintenanceId),
          hideMaintenanceTab: false,
        };
      }

      const openRecords = records.filter(
        record =>
          String(record?.version ?? record?.Version ?? '')
            .trim()
            .toUpperCase() !== 'CLOSED',
      );

      const latestOpenRecord = [...openRecords].sort(
        (a, b) => getMaintenanceId(b) - getMaintenanceId(a),
      )[0];

      if (!latestOpenRecord) {
        return {
          moldReceivingData: receiving,
          moldMaintenanceData: null,
          hideMaintenanceTab: true,
        };
      }

      const maintenanceId = getMaintenanceId(latestOpenRecord);
      const detail = (await loadDetail(maintenanceId)) || latestOpenRecord;

      return {
        moldReceivingData: receiving,
        moldMaintenanceData: toFormMaintenance(detail, maintenanceId),
        hideMaintenanceTab: false,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load mold maintenance data');
    }
  },
);

export const fetchMoldMaintenanceByToolId = createAsyncThunk(
  'moldMaintenance/fetchMoldMaintenanceByToolId',
  async (toolID, { rejectWithValue }) => {
    try {
      const response = await MoldService.getAllMoldMaintenanceByToolId(toolID);
      if (response.status === 200) {
        return mapMaintenanceListSubmittedByFromApi(response.response);
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to load mold maintenance data',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load  mold maintenance data');
    }
  },
);

export const fetchMoldReceivingWithFilters = () => (dispatch, getState) => {
  const filters = getState().moldmaintenance.filters;
  dispatch(
    fetchMoldReceiving({
      searchText: filters.searchText,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
      isReleased: filters?.isReleased,
      recordFilter: filters?.recordFilter,
    }),
  );
};

export const fetchMoldMaintenanceWithStatus = () => (dispatch, getState) => {
  const filters = getState().moldmaintenance.filters;
  dispatch(
    fetchMoldsByStatus({
      isReleased: filters?.isReleased,
      recordFilter: filters?.recordFilter,
    }),
  );
};

export const fetchMoldMaintenanceWithOpenFilter = () => (dispatch, getState) => {
  const filters = getState().moldmaintenance.filters;
  dispatch(
    fetchMoldReceiving({
      isReleased: filters?.isReleased ?? 1,
      recordFilter: filters?.recordFilter,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate,
    }),
  );
};

export const { reducer, actions } = createSlice({
  name: 'mold-maintenance',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setFromDate: (state, action) => {
      state.filters.fromDate = action.payload;
    },
    setToDate: (state, action) => {
      state.filters.toDate = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.isReleased = action.payload;
    },
    setRecordFilter: (state, action) => {
      state.filters.recordFilter = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMoldReceiving.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMoldReceiving.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchMoldReceiving.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMoldsByStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMoldsByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchMoldsByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addMoldMaintenance.fulfilled, (state, action) => {
        const newMoldMaintenance = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newMoldMaintenance, ...state.data];
      })
      .addCase(addMoldMaintenance.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editMoldMaintenance.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editMoldMaintenance.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editMoldMaintenanceData.pending, state => {
        state.maintenanceLoading = true;
      })
      .addCase(editMoldMaintenanceData.fulfilled, state => {
        state.maintenanceLoading = false;
      })
      .addCase(editMoldMaintenanceData.rejected, state => {
        state.maintenanceLoading = false;
      })
      .addCase(fetchMoldMaintenanceByToolId.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchMoldMaintenanceByToolId.fulfilled, (state, action) => {
        state.moldMaintenanceRecordsData = action.payload;
        state.loading = false;
      })
      .addCase(fetchMoldMaintenanceByToolId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(
    addMoldMaintenance.rejected,
    editMoldMaintenance.rejected,
    editMoldMaintenanceData.rejected,
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addMoldMaintenance.fulfilled, editMoldMaintenance.fulfilled),
  effect: async (action, listenerApi) => {
    // Close modal and show success message
    listenerApi.dispatch(closeModal(MOLDMAINTENANCE_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Mold Maintenance success',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editMoldMaintenanceData.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(MOLDMAINTENANCE_ADD_EDIT, action.payload));
  },
});

redux.injectReducer(STORE_KEY, reducer);
